import React from 'react';
import { PostInsights, Member } from '@frontend/gql/social/graphql';
import { HandWaveIcon, EyeIcon, CalendarIcon } from '@revfluence/fresh-icons/regular/esm';
import { StarsIcon } from '@revfluence/fresh-icons/solid/esm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { useSocialMediaThumbnail } from '@frontend/applications/Social/hooks/useSocialMediaThumbnail';
import {
  PostCard,
  PostCardMedia,
  PostCardSkeleton,
  PostCardBackground,
  PostCardContent,
  PostCardImageContainer,
  PostCardImageContent,
  PostCardNetworkIcon,
  PostCardImage,
  PostCardPlaceholder,
  PostCardGradient,
  PostCardHeader,
  PostCardAvatar,
  PostCardAvatarImage,
  PostCardAvatarFallback,
  PostCardAvatarName,
  PostCardFooter,
  PostCardActions,
  PostCardStats,
  PostCardStat,
  PostCardAvatarContainer,
  PostCardHeaderRow,
  PostCardStatText,
} from '@frontend/applications/Social/components/post-card';

type TopPostCardProps = {
  post?: PostInsights;
};

const TopPostCard = React.forwardRef<HTMLDivElement, TopPostCardProps>(({ post }, ref) => {
  const { imageUrl, isLoading, onImageError } = useSocialMediaThumbnail({
    postUrl: post?.postPermalink ?? '',
    postImageUrl: post?.postImageUrl ?? '',
  });

  // If `post` is optional, bail out immediately.
  if (!post) return null;

  const member: Partial<Member> = post.member ?? {};
  const memberName = member.name ?? '';
  const profilePicture = member.profilePicture ?? '';

  // Parse tags from post.postCaption
  const tags = post.postCaption?.match(/#([\w_]+)/g) || [];
  const tagsVisible = tags.slice(0, 3);

  const showLoading = isLoading;
  const showImage = imageUrl && !showLoading;
  const showPlaceholder = !showImage && !showLoading;

  const handleOpenInNewTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (post.postPermalink) {
      window.open(post.postPermalink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <PostCard ref={ref}>
      <PostCardMedia>
        {showLoading && <PostCardSkeleton />}
        {showImage && (
          <>
            <PostCardBackground imageUrl={imageUrl} />
            <PostCardImageContainer>
              <PostCardImageContent>
                <PostCardImage src={imageUrl} onError={onImageError} alt="Post preview" />
              </PostCardImageContent>
            </PostCardImageContainer>
          </>
        )}
        {showPlaceholder && <PostCardPlaceholder />}
        <PostCardGradient />
      </PostCardMedia>

      <PostCardContent>
        <PostCardHeader>
          <PostCardHeaderRow>
            <PostCardAvatarContainer>
              <PostCardAvatar networkIcon={<PostCardNetworkIcon network={post.entityNetwork} />}>
                <PostCardAvatarImage src={profilePicture} alt={memberName} />
                <PostCardAvatarFallback name={memberName} />
              </PostCardAvatar>
              <PostCardAvatarName>{memberName}</PostCardAvatarName>
            </PostCardAvatarContainer>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleOpenInNewTab}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Open post in new tab"
                  >
                    <FontAwesomeIcon icon={faUpRightFromSquare} className="h-4 w-4 text-gray-500" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Open in new tab</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </PostCardHeaderRow>
        </PostCardHeader>

        <div className="flex-grow" />

        <PostCardFooter>
          {/* Boost Eligible Badge */}
          {post.advertisableMediaPermissions?.some((permission) => permission?.hasPermissions) && (
            <div className="flex justify-start">
              <Badge variant="secondary" className="bg-green-4 text-white text-xs px-2 py-1 flex items-center gap-1">
                <StarsIcon className="h-3 w-3" />
                Boost Eligible
              </Badge>
            </div>
          )}
          {/* Tags Row */}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {tagsVisible.map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs px-2 py-1 bg-grey-2 text-grey-9 border-grey-4">
                  {tag}
                </Badge>
              ))}
              {tagsVisible.length < tags.length && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="outline" className="text-xs px-2 py-1 bg-grey-2 text-grey-9 border-grey-4">
                        +{tags.length - tagsVisible.length}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{tags.slice(tagsVisible.length).join(', ')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
          {/* Date and Stats Row */}
          <PostCardActions>
            {/* Date posted */}
            <PostCardStatText
              icon={<CalendarIcon className="h-4 w-4" />}
              text={
                post.postDate
                  ? new Date(post.postDate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'Unknown'
              }
            />

            {/* Stats */}
            <PostCardStats>
              {/* Always show impressions (views in design) */}
              {post.total?.impressions != null && (
                <PostCardStat icon={<EyeIcon className="h-4 w-4" />} value={post.total.impressions} />
              )}
              {/* Show engagement on sm+ screens */}
              {post.total?.engagements != null && (
                <PostCardStat
                  icon={<HandWaveIcon className="h-4 w-4" />}
                  value={post.total.engagements}
                  className="hidden sm:flex"
                />
              )}
            </PostCardStats>
          </PostCardActions>
        </PostCardFooter>
      </PostCardContent>
    </PostCard>
  );
});

TopPostCard.displayName = 'TopPostCard';

export default TopPostCard;
